/* Collection grid: sort/filter submit, wishlist heart, and Load more.
   Load more appends the next Shopify page. The button is removed once
   every product in the current result is on the page. */
(() => {
  'use strict';
  if (customElements.get('collection-index')) return;

  const WISHLIST_KEY = 'magni:mto:wishlist';

  function readWishlist() {
    try {
      return JSON.parse(localStorage.getItem(WISHLIST_KEY)) || [];
    } catch (error) {
      return [];
    }
  }

  function writeWishlist(list) {
    try {
      localStorage.setItem(WISHLIST_KEY, JSON.stringify(list));
    } catch (error) {
      /* private mode — the pressed state still updates in the DOM */
    }
  }

  class CollectionIndex extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.events = new AbortController();
      const { signal } = this.events;
      this.dataset.enhanced = 'true';
      this.syncWishlist();
      this.placeFeatures();
      this.settleMedia(this);
      this.bindFeatureVideos();
      this.bindGalleries(this);

      this.bindFilters(signal);

      const form = this.querySelector('[data-collection-form]');
      if (form) {
        const context = new URL(location.href);
        if (context.searchParams.has('preview_theme_id')) {
          const hidden = document.createElement('input');
          hidden.type = 'hidden';
          hidden.name = 'preview_theme_id';
          hidden.value = context.searchParams.get('preview_theme_id');
          hidden.dataset.previewContext = '';
          form.append(hidden);
        }
        const validatePrice = () => {
          form.querySelectorAll('[data-price-range]').forEach((range) => {
            const min = range.querySelector('[data-price-min]');
            const max = range.querySelector('[data-price-max]');
            const invalid = min.value !== '' && max.value !== '' && Number(min.value) > Number(max.value);
            min.setCustomValidity(invalid ? this.dataset.priceError : '');
          });
        };
        form.addEventListener('input', validatePrice, { signal });
        form.addEventListener('change', (event) => {
          if (event.target.matches('[data-sort]')) {
            validatePrice();
            form.requestSubmit();
          }
        }, { signal });
        form.addEventListener('submit', (event) => {
          validatePrice();
          if (!form.reportValidity()) {
            event.preventDefault();
            this.openFilters?.();
            return;
          }
          form.querySelectorAll('input[type="number"]').forEach((input) => {
            if (input.value === '') input.disabled = true;
          });
        }, { signal });
      }

      this.addEventListener('click', (event) => {
        const heart = event.target.closest('[data-cidx-wishlist]');
        if (heart && this.contains(heart)) {
          event.preventDefault();
          this.toggleWishlist(heart);
          return;
        }
        const prev = event.target.closest('[data-cidx-prev]');
        const next = event.target.closest('[data-cidx-next]');
        if ((prev || next) && this.contains(prev || next)) {
          event.preventDefault();
          event.stopPropagation();
          this.stepGallery((prev || next).closest('[data-cidx-gallery]'), next ? 1 : -1);
          return;
        }
        const more = event.target.closest('[data-cidx-more]');
        if (more && this.contains(more)) {
          event.preventDefault();
          this.loadMore(more);
        }
      }, { signal });

      window.addEventListener('pageshow', () => {
        form?.querySelectorAll('input[type="number"]').forEach((input) => {
          input.disabled = false;
        });
      }, { signal });
    }

    disconnectedCallback() {
      this.featureVideoObserver?.disconnect();
      this.featureVideoObserver = null;
      this.events?.abort();
      this.events = null;
      document.body.classList.remove('cidx-filter-open');
      this.querySelectorAll('[data-preview-context]').forEach((input) => input.remove());
    }

    bindFilters(signal) {
      const drawer = this.querySelector('[data-cidx-filter]');
      const openBtn = this.querySelector('[data-cidx-filters-open]');
      const panel = drawer?.querySelector('.cidx-filter__panel');
      if (!drawer || !openBtn || !panel) return;

      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      let frame = 0;
      let closeTimer = 0;
      let lastFocus = openBtn;

      const settle = () => {
        if (drawer.classList.contains('is-open')) return;
        drawer.classList.remove('is-closing');
        drawer.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('cidx-filter-open');
        if (drawer.contains(document.activeElement) || document.activeElement === document.body) lastFocus.focus();
      };

      const open = () => {
        const token = ++frame;
        window.clearTimeout(closeTimer);
        lastFocus = openBtn;
        drawer.setAttribute('aria-hidden', 'false');
        openBtn.setAttribute('aria-expanded', 'true');
        document.body.classList.add('cidx-filter-open');
        if (reduce) {
          drawer.classList.add('is-open');
          drawer.querySelector('.cidx-filter__close')?.focus();
          return;
        }
        drawer.classList.add('is-closing');
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (token !== frame) return;
            drawer.classList.add('is-open');
            drawer.classList.remove('is-closing');
            drawer.querySelector('.cidx-filter__close')?.focus();
          });
        });
      };

      const close = () => {
        if (!drawer.classList.contains('is-open') && !drawer.classList.contains('is-closing')) return;
        frame += 1;
        drawer.classList.remove('is-open');
        openBtn.setAttribute('aria-expanded', 'false');
        if (reduce) {
          settle();
          return;
        }
        drawer.classList.add('is-closing');
        window.clearTimeout(closeTimer);
        closeTimer = window.setTimeout(settle, 500);
      };

      this.openFilters = open;
      openBtn.addEventListener('click', open, { signal });
      drawer.addEventListener('click', (event) => {
        if (event.target.closest('[data-cidx-filter-close]')) close();
      }, { signal });
      panel.addEventListener('transitionend', (event) => {
        if (event.target !== panel || event.propertyName !== 'transform') return;
        if (drawer.classList.contains('is-open')) return;
        window.clearTimeout(closeTimer);
        settle();
      }, { signal });
      this.addEventListener('keydown', (event) => {
        if (!drawer.classList.contains('is-open')) return;
        if (event.key === 'Escape') {
          event.preventDefault();
          close();
          return;
        }
        if (event.key !== 'Tab') return;
        const focusable = [...drawer.querySelectorAll('a, button, input, select, textarea')].filter((el) => {
          return !el.disabled && el.tabIndex !== -1 && el.getClientRects().length > 0;
        });
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }, { signal });
    }

    syncWishlist() {
      const saved = readWishlist();
      this.querySelectorAll('[data-cidx-wishlist]').forEach((button) => {
        const on = saved.indexOf(button.dataset.cidxWishlist) !== -1;
        button.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
    }

    toggleWishlist(button) {
      const handle = button.dataset.cidxWishlist;
      const saved = readWishlist();
      const at = saved.indexOf(handle);
      if (at === -1) saved.push(handle);
      else saved.splice(at, 1);
      writeWishlist(saved);
      this.syncWishlist();
    }

    bindGalleries(root) {
      root.querySelectorAll('[data-cidx-gallery]').forEach((media) => {
        if (media.dataset.cidxBound === 'true' || media.dataset.cidxVideo === 'true') return;
        media.dataset.cidxBound = 'true';
        media.addEventListener('pointerenter', (event) => {
          if (event.pointerType !== 'mouse') return;
          const frames = media.querySelectorAll('[data-cidx-frame]');
          if (frames.length < 2) return;
          this.showFrame(media, 1);
        });
        media.addEventListener('pointerleave', (event) => {
          if (event.pointerType !== 'mouse') return;
          this.showFrame(media, 0);
        });
      });
    }

    showFrame(media, index) {
      const frames = [...media.querySelectorAll('[data-cidx-frame]')];
      if (!frames.length) return;
      const next = (index + frames.length) % frames.length;
      frames.forEach((frame, frameIndex) => {
        const active = frameIndex === next;
        frame.classList.toggle('is-active', active);
        if (active) frame.removeAttribute('aria-hidden');
        else frame.setAttribute('aria-hidden', 'true');
      });
    }

    stepGallery(media, step) {
      if (!media || media.dataset.cidxVideo === 'true') return;
      const frames = [...media.querySelectorAll('[data-cidx-frame]')];
      if (frames.length < 2) return;
      const current = frames.findIndex((frame) => frame.classList.contains('is-active'));
      this.showFrame(media, (current < 0 ? 0 : current) + step);
    }

    settleMedia(root) {
      if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      root.querySelectorAll('video').forEach((video) => {
        video.removeAttribute('autoplay');
        video.pause();
      });
    }

    bindFeatureVideos() {
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const videos = [...this.querySelectorAll('[data-cidx-feature] video')];
      if (!videos.length || reduced) return;
      const play = (video) => {
        if (video.closest('[hidden]')) return;
        video.muted = true;
        video.loop = true;
        const attempt = video.play();
        if (attempt) attempt.catch(() => {});
      };
      if (!('IntersectionObserver' in window)) {
        videos.forEach(play);
        return;
      }
      this.featureVideoObserver?.disconnect();
      this.featureVideoObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) play(entry.target);
          else entry.target.pause();
        });
      }, { threshold: 0.35 });
      videos.forEach((video) => this.featureVideoObserver.observe(video));
    }

    placeFeatures() {
      const grid = this.querySelector('[data-cidx-grid]');
      if (!grid || grid.hasAttribute('data-cidx-plain')) return;
      const cards = [...grid.querySelectorAll('[data-cidx-card]')];
      const features = [...grid.querySelectorAll('[data-cidx-feature]')];
      const featureCount = features.length;
      cards.forEach((card, index) => {
        const i = index + 1;
        const desktopGroups = Math.min(featureCount, Math.floor((i - 1) / 6));
        const mobileGroups = Math.min(featureCount, Math.floor((i - 1) / 4));
        card.style.setProperty('--cidx-d', String(i + desktopGroups));
        card.style.setProperty('--cidx-m', String(i + mobileGroups));
        card.classList.add('cidx-placed');
      });
      features.forEach((feature, index) => {
        const k = index + 1;
        const desktop = cards.length >= 6 * k;
        const mobile = cards.length >= 4 * k;
        feature.style.setProperty('--cidx-d', String(7 * k));
        feature.style.setProperty('--cidx-m', String(5 * k));
        feature.classList.add('cidx-placed');
        feature.classList.remove('cidx-show-desktop', 'cidx-show-mobile');
        if (!desktop && !mobile) {
          feature.hidden = true;
          return;
        }
        feature.hidden = false;
        if (mobile && !desktop) feature.classList.add('cidx-show-mobile');
        else if (desktop && !mobile) feature.classList.add('cidx-show-desktop');
      });
    }

    syncDisplayedCount() {
      const el = this.querySelector('[data-cidx-product-count]');
      if (!el) return;
      const count = this.querySelectorAll('[data-cidx-card]:not([hidden])').length;
      if (count === 1) {
        el.textContent = el.dataset.labelOne || String(count);
        return;
      }
      const template = el.dataset.labelOther || '';
      el.textContent = template ? template.replace('2', String(count)) : String(count);
    }

    syncLoadProgress() {
      const el = this.querySelector('[data-cidx-count]');
      if (!el || !el.dataset.label) return;
      const shown = this.querySelectorAll('[data-cidx-card]:not([hidden])').length;
      const total = el.dataset.cardTotal || String(shown);
      el.textContent = el.dataset.label.replace('__SHOWN__', String(shown)).replace('__TOTAL__', String(total));
    }

    async loadMore(button) {
      if (button.dataset.loading === 'true') return;
      const next = button.getAttribute('href');
      if (!next) return;
      button.dataset.loading = 'true';
      button.setAttribute('aria-busy', 'true');
      try {
        const url = new URL(next, window.location.href);
        url.searchParams.set('section_id', this.dataset.sectionId);
        const response = await fetch(url.href, { headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error('load');
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const grid = this.querySelector('[data-cidx-grid]');
        const incoming = doc.querySelector('[data-cidx-grid]');
        const slots = incoming ? [...incoming.querySelectorAll('[data-cidx-slot]')] : [];
        if (grid && slots.length) {
          /* Parse in this document. Moving <img> nodes out of DOMParser makes
             WebKit ignore object-fit, so the new photos stretch. */
          grid.insertAdjacentHTML('beforeend', slots.map((slot) => slot.outerHTML).join(''));
          this.placeFeatures();
        }
        this.syncDisplayedCount();
        this.syncLoadProgress();
        const fresh = doc.querySelector('[data-cidx-more]');
        const wrap = button.closest('[data-cidx-more-wrap]');
        if (fresh && fresh.getAttribute('href')) {
          button.setAttribute('href', fresh.getAttribute('href'));
        } else if (wrap) {
          wrap.hidden = true;
        }
        this.settleMedia(grid || this);
        this.bindFeatureVideos();
        this.bindGalleries(grid || this);
        this.syncWishlist();
      } catch (error) {
        window.location.assign(next);
      } finally {
        button.dataset.loading = 'false';
        button.removeAttribute('aria-busy');
      }
    }
  }

  customElements.define('collection-index', CollectionIndex);
})();
